import { useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import Breadcrumb from "@/components/feature/Breadcrumb";
import ComicCard from "@/components/base/ComicCard";
import Icon from "@/components/base/Icon";
import { useFavorites } from "@/hooks/useFavorites";
import { useReadingProgress } from "@/hooks/useReadingProgress";
import { useSeo } from "@/hooks/useSeo";
import {
  formatViews,
  getCategoryById,
  getChapters,
  getComicById,
  getRelatedComics,
  statusLabel,
} from "@/utils/comic";

export default function ComicDetailPage() {
  const { comicId } = useParams();
  const comic = getComicById(comicId);
  const [expanded, setExpanded] = useState(false);
  const [showAllChapters, setShowAllChapters] = useState(false);
  const { isFavorite, toggleFavorite } = useFavorites();
  const { progress } = useReadingProgress();

  const chapters = useMemo(() => (comic ? getChapters(comic) : []), [comic]);
  const related = useMemo(() => (comic ? getRelatedComics(comic, 4) : []), [comic]);

  useSeo({
    title: comic ? `${comic.title} - 作者：${comic.author} - 羞羞色漫` : "漫画详情 - 羞羞色漫",
    description: comic
      ? `${comic.title}（作者：${comic.author}）${comic.description.slice(0, 60)} 连载状态：${statusLabel(comic.status)}。羞羞色漫在线阅读。`
      : "",
    keywords: comic
      ? `${comic.title},${comic.author},羞羞色漫,${comic.tags.join(",")},在线漫画`
      : undefined,
  });

  if (!comic) {
    return (
      <div className="w-full px-4 md:px-8 py-20 text-center">
        <Icon name="ri-emotion-sad-line" className="w-12 h-12 text-foreground-400 mx-auto" />
        <p className="mt-4 text-foreground-700">未找到这部漫画</p>
        <Link
          to="/category/all"
          className="mt-4 inline-flex items-center gap-1 px-4 py-2 rounded-md bg-primary-500 text-background-50 text-sm cursor-pointer"
        >
          浏览全部分类
        </Link>
      </div>
    );
  }

  const category = getCategoryById(comic.category);
  const favorite = isFavorite(comic.id);
  const lastChapterId = progress[comic.id];
  const latestChapter = chapters[0];
  const continueChapterId = lastChapterId || latestChapter?.id;
  const visibleChapters = showAllChapters ? chapters : chapters.slice(0, 12);

  return (
    <div className="w-full px-4 md:px-8 py-6">
      <Breadcrumb
        items={[
          { label: "首页", to: "/" },
          { label: "分类", to: "/category/all" },
          { label: category?.name || "漫画", to: `/category/${comic.category}` },
          { label: comic.title },
        ]}
      />

      <section className="mt-6 grid grid-cols-1 lg:grid-cols-[300px_1fr] gap-6 lg:gap-8">
        <div className="flex flex-col gap-4">
          <div className="w-full max-w-[300px] mx-auto lg:mx-0 aspect-[3/4] rounded-lg overflow-hidden border border-background-200 bg-background-200">
            <img
              src={comic.cover}
              alt={`${comic.title}漫画封面`}
              title={`${comic.title} - 作者：${comic.author}`}
              className="w-full h-full object-cover object-top"
            />
          </div>
          <div className="flex items-center gap-3">
            <Link
              to={`/read/${comic.id}/${continueChapterId}`}
              className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-md bg-primary-500 text-background-50 text-sm font-medium whitespace-nowrap cursor-pointer transition-colors hover:bg-primary-600"
            >
              <Icon name="ri-play-circle-line" className="w-4 h-4" />
              {lastChapterId ? "继续阅读" : "开始阅读"}
            </Link>
            <button
              type="button"
              onClick={() => toggleFavorite(comic.id)}
              aria-label={favorite ? "取消收藏" : "收藏漫画"}
              className={`inline-flex items-center justify-center gap-1.5 px-4 py-2.5 rounded-md text-sm font-medium whitespace-nowrap cursor-pointer transition-colors ${
                favorite
                  ? "bg-primary-100 text-primary-700"
                  : "border border-background-300 text-foreground-700 hover:border-primary-300 hover:text-primary-600"
              }`}
            >
              <Icon name={favorite ? "ri-heart-3-fill" : "ri-heart-3-line"} className="w-4 h-4" />
              {favorite ? "已收藏" : "收藏"}
            </button>
          </div>
          <p className="text-center lg:text-left text-[11px] text-foreground-400">
            收藏保存在本机浏览器（演示功能）
          </p>
        </div>

        <div className="min-w-0">
          <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950">
            {comic.title}
          </h1>

          <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-3">
            <div className="rounded-lg border border-background-200 bg-background-50 p-3">
              <p className="text-xs text-foreground-400">作者</p>
              <p className="mt-1 text-sm font-medium text-foreground-900">{comic.author}</p>
            </div>
            <div className="rounded-lg border border-background-200 bg-background-50 p-3">
              <p className="text-xs text-foreground-400">状态</p>
              <p className="mt-1 text-sm font-medium text-foreground-900">
                {statusLabel(comic.status)}
              </p>
            </div>
            <div className="rounded-lg border border-background-200 bg-background-50 p-3">
              <p className="text-xs text-foreground-400">人气</p>
              <p className="mt-1 text-sm font-medium text-foreground-900">
                {formatViews(comic.views)}
              </p>
            </div>
            <div className="rounded-lg border border-background-200 bg-background-50 p-3">
              <p className="text-xs text-foreground-400">更新时间</p>
              <p className="mt-1 text-sm font-medium text-foreground-900">{comic.updatedAt}</p>
            </div>
          </div>

          <div className="mt-5 flex flex-wrap items-center gap-2">
            <span className="px-2.5 py-1 rounded-md bg-accent-100 text-accent-800 text-xs font-medium">
              {comic.region}
            </span>
            {category && (
              <Link
                to={`/category/${category.id}`}
                className="px-2.5 py-1 rounded-md bg-primary-100 text-primary-700 text-xs font-medium cursor-pointer transition-colors hover:bg-primary-200"
              >
                {category.name}
              </Link>
            )}
            {comic.tags.map((tag) => (
              <Link
                key={tag}
                to={`/search?q=${encodeURIComponent(tag)}`}
                className="px-2.5 py-1 rounded-md bg-background-100 text-foreground-600 text-xs cursor-pointer transition-colors hover:bg-background-200"
              >
                {tag}
              </Link>
            ))}
            <span className="px-2.5 py-1 rounded-md bg-background-100 text-foreground-600 text-xs flex items-center gap-1">
              <Icon name="ri-star-fill" className="w-3 h-3 text-accent-500" />
              {comic.rating} 分
            </span>
          </div>

          <div className="mt-6">
            <h2 className="font-heading text-base font-semibold text-foreground-950 mb-2">
              作品简介
            </h2>
            <p
              className={`text-sm text-foreground-600 leading-relaxed ${
                expanded ? "" : "line-clamp-3"
              }`}
            >
              {comic.description}
            </p>
            <button
              type="button"
              onClick={() => setExpanded((v) => !v)}
              className="mt-2 inline-flex items-center gap-1 text-sm text-primary-600 cursor-pointer transition-colors hover:text-primary-700"
            >
              {expanded ? "收起" : "展开全文"}
              <Icon
                name={expanded ? "ri-arrow-up-s-line" : "ri-arrow-down-s-line"}
                className="w-4 h-4"
              />
            </button>
          </div>

          <div className="mt-6">
            <div className="flex items-center justify-between gap-3 mb-3">
              <h2 className="font-heading text-base font-semibold text-foreground-950">
                章节目录
                <span className="ml-2 text-xs font-normal text-foreground-400">
                  更新至 {comic.chapterCount} 话
                </span>
              </h2>
              <span className="text-xs text-foreground-400">最新章节优先</span>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
              {visibleChapters.map((chapter, index) => (
                <Link
                  key={chapter.id}
                  to={`/read/${comic.id}/${chapter.id}`}
                  className={`flex items-center justify-between gap-2 px-3 py-2 rounded-md border text-sm cursor-pointer transition-colors ${
                    index === 0
                      ? "border-primary-300 bg-primary-50 text-primary-700"
                      : "border-background-200 bg-background-50 text-foreground-700 hover:border-primary-300 hover:text-primary-600"
                  }`}
                >
                  <span className="truncate">{chapter.title}</span>
                  {index === 0 && (
                    <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-primary-500 text-background-50 shrink-0">
                      最新
                    </span>
                  )}
                </Link>
              ))}
            </div>
            {chapters.length > 12 && (
              <button
                type="button"
                onClick={() => setShowAllChapters((v) => !v)}
                className="mt-3 w-full py-2.5 rounded-md border border-background-300 text-sm text-foreground-700 cursor-pointer transition-colors hover:border-primary-300 hover:text-primary-600"
              >
                {showAllChapters ? "收起章节" : `展开全部 ${chapters.length} 话`}
              </button>
            )}
          </div>

          <div className="mt-6 flex flex-wrap gap-3">
            <Link
              to={`/category/${comic.category}`}
              className="inline-flex items-center gap-1 px-4 py-2 rounded-md border border-background-300 text-sm text-foreground-700 cursor-pointer transition-colors hover:border-primary-300 hover:text-primary-600"
            >
              <Icon name="ri-arrow-left-line" className="w-4 h-4" />
              返回{category?.name || "分类"}
            </Link>
          </div>
        </div>
      </section>

      {related.length > 0 && (
        <section className="mt-12">
          <div className="flex items-center gap-3 mb-5">
            <span className="w-10 h-10 rounded-lg bg-accent-100 text-accent-700 flex items-center justify-center">
              <Icon name="ri-magic-line" className="w-5 h-5" />
            </span>
            <div>
              <h2 className="font-heading text-xl font-bold text-foreground-950">相关推荐</h2>
              <p className="text-xs text-foreground-500">来自相同分类或标签的热门作品</p>
            </div>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-5">
            {related.map((item) => (
              <ComicCard key={item.id} comic={item} />
            ))}
          </div>
        </section>
      )}
    </div>
  );
}